import { Link } from 'react-router-dom';
import { AlertTriangle, Camera, Info, Lock, Scale, ShieldCheck } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { Switch } from '@/components/forms/Switch';
import { cn } from '@/lib/cn';
import type { ConsentConfig, DeploymentOverrides } from '@/lib/api/xurvey';
import { useT } from '@/lib/i18n/useT';

type ConsentOverrides = NonNullable<DeploymentOverrides['consent']>;

interface Props {
  /** Lo que el backend deriva para el despliegue que se previsualiza. */
  config: ConsentConfig | undefined;
  loading: boolean;
  /** Lo que este despliegue decide (fotos, menores), del borrador. */
  consent: ConsentOverrides | undefined;
  onConsent: (delta: ConsentOverrides) => void;
  /**
   * ¿Es un enlace de Xotski (regla 1, `esEnlaceDeXotski`)? `undefined` = aún no
   * se sabe: la tarjeta espera con el esqueleto. `false` = la vista de siempre.
   */
  esXotski?: boolean;
}

/**
 * La tarjeta «Consentimiento» del Design Journey.
 *
 * Aquí NO se configura lo legal: se ENSEÑA lo que la capa legal deriva para
 * este despliegue —qué casillas verá el invitado y por qué no verá otras— y se
 * decide lo único que es de la activación: si se hacen fotos y si puede haber
 * menores. Contacto, móvil y premio salen de la encuesta; age gate, marketing y
 * SMS de la página Legal de la marca. Un instrumento que la marca activó pero
 * que la encuesta no recoge sale gris con su motivo, no desaparece sin más.
 *
 * Los bloqueos (premio sin Reglas oficiales, age gate de marca sin paso de
 * edad) se dicen arriba con su CTA a la página Legal: no se arreglan aquí y
 * un «Activar» que fallara con un 422 seco sería un misterio.
 *
 * En los enlaces de Xotski la tarjeta enseña sólo lo que el invitado va a ver:
 * las líneas de Official Rules, SMS y cesión de imagen salen únicamente si el
 * motor las deriva, y fotos y menores sólo si el evento (o su borrador) ya
 * tiene uno puesto, para poder devolverlo a su sitio. Lo puesto se sigue
 * viendo siempre y los bloqueos nunca se ocultan; xeck-in y el resto, igual.
 */
export function ConsentCard({ config, loading, consent, onConsent, esXotski }: Props) {
  const t = useT();
  const photos = consent?.photos ?? config?.inputs.photos ?? 'none';
  const minors = consent?.minorsPossible ?? config?.inputs.minorsPossible ?? false;

  const bloqueos = config?.issues.filter((i) => i.severity === 'block') ?? [];
  const avisos =
    config?.issues.filter(
      (i) => i.severity !== 'block' && !(esXotski && i.code === 'XY_LEGAL_SMS_HIDDEN_NO_MOBILE'),
    ) ?? [];
  // Fotos y menores puestos (lo guardado o el borrador): en Xotski, entonces sí se ven.
  const noPorDefecto =
    (config?.inputs.photos ?? 'none') !== 'none' ||
    config?.inputs.minorsPossible === true ||
    (consent?.photos ?? 'none') !== 'none' ||
    consent?.minorsPossible === true;
  const verControles = !esXotski || noPorDefecto;
  const has = (id: string) => !!config?.instruments.some((i) => i.id === id);

  const todas: Array<{ key: string; on: boolean; motivo?: string; required?: boolean }> = config
    ? [
        { key: 'notice', on: true },
        { key: 'terms', on: true, required: true },
        { key: 'rules', on: has('official_rules_link'), motivo: has('official_rules_link') ? undefined : t('designJourney.consent.why.noPrize') },
        {
          key: 'marketing',
          on: has('marketing_optin'),
          motivo: has('marketing_optin')
            ? undefined
            : !config.inputs.marketingEnabled
              ? t('designJourney.consent.why.brandOff')
              : t('designJourney.consent.why.noContact'),
        },
        {
          key: 'sms',
          on: has('sms_optin'),
          motivo: has('sms_optin')
            ? undefined
            : !config.inputs.smsEnabled
              ? t('designJourney.consent.why.brandOff')
              : t('designJourney.consent.why.noMobile'),
        },
        {
          key: 'media',
          on: has('media_tier_b'),
          motivo: has('media_tier_b')
            ? undefined
            : photos === 'featured'
              ? t('designJourney.consent.why.minors')
              : t('designJourney.consent.why.noFeatured'),
        },
        {
          key: 'ageGate',
          on: has('age_gate'),
          required: true,
          motivo: has('age_gate') ? undefined : t('designJourney.consent.why.brandOff'),
        },
      ]
    : [];
  // En Xotski, las líneas apagadas de reglas, SMS e imagen no se enseñan.
  const filas = esXotski ? todas.filter((f) => f.on || !['rules', 'sms', 'media'].includes(f.key)) : todas;

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2">
        <Scale className="h-4 w-4 text-[var(--color-accent-ink)]" />
        <h3 className="text-sm font-semibold text-[var(--color-fg-primary)]">
          {t('designJourney.consent.title')}
        </h3>
        <span className="ml-auto text-[var(--color-fg-subtle)]" title={t('designJourney.legalLocked')}>
          <Lock className="h-3.5 w-3.5" />
        </span>
      </div>
      <p className="mt-1 text-xs leading-relaxed text-[var(--color-fg-muted)]">
        {esXotski ? t('designJourney.consent.descriptionXotski') : t('designJourney.consent.description')}
      </p>

      {loading || esXotski === undefined ? <Skeleton className="mt-3 h-24 w-full" /> : null}
      {esXotski === undefined ? null : (
        <>

      {bloqueos.length > 0 ? (
        <div
          role="alert"
          className="mt-3 rounded-lg border border-[var(--color-error)] bg-[color-mix(in_srgb,var(--color-error)_10%,transparent)] p-3"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-[var(--color-error)]" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-[var(--color-fg-primary)]">
                {t('designJourney.consent.blockedTitle')}
              </p>
              <ul className="mt-1 space-y-1 text-xs leading-relaxed text-[var(--color-fg-muted)]">
                {bloqueos.map((b) => (
                  <li key={b.code}>{b.message}</li>
                ))}
              </ul>
              <Link
                to="/settings/legal"
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--color-accent-ink)] underline-offset-2 hover:underline"
              >
                {t('designJourney.consent.cta')}
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {avisos.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {avisos.map((a) => (
            <li key={a.code} className="flex items-start gap-2 text-xs leading-relaxed text-[var(--color-fg-muted)]">
              <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-[var(--color-fg-subtle)]" />
              <span>{a.message}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Lo que decide ESTE despliegue. */}
      {verControles ? (
      <div className="mt-4 grid gap-3">
        {esXotski ? (
          <p className="flex items-start gap-2 text-xs leading-relaxed text-[var(--color-fg-muted)]">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-[var(--color-fg-subtle)]" />
            <span>{t('designJourney.consent.setHereNote')}</span>
          </p>
        ) : null}
        <label className="block">
          <span className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">
            <Camera className="h-3.5 w-3.5" />
            {t('designJourney.consent.photos')}
          </span>
          <Select
            value={photos}
            onChange={(e) => onConsent({ photos: e.target.value as ConsentOverrides['photos'], minorsPossible: minors })}
            className="mt-1"
          >
            <option value="none">{t('designJourney.consent.photos.none')}</option>
            <option value="ambient">{t('designJourney.consent.photos.ambient')}</option>
            <option value="featured">{t('designJourney.consent.photos.featured')}</option>
          </Select>
        </label>
        <Switch
          checked={minors}
          onChange={(v) => onConsent({ photos, minorsPossible: v })}
          label={t('designJourney.consent.minors')}
          description={t('designJourney.consent.minorsDescription')}
        />
      </div>
      ) : null}

      {/* Lo que verá el invitado: derivado, de sólo lectura. */}
      {config ? (
        <ul className="mt-4 divide-y divide-[var(--color-border-subtle)]">
          {filas.map((f) => (
            <li key={f.key} className="flex items-start gap-2 py-1.5">
              <span
                className={cn(
                  'mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full text-[10px]',
                  f.on
                    ? 'bg-[var(--color-accent-10)] text-[var(--color-accent-ink)]'
                    : 'bg-[var(--color-bg-elevated)] text-[var(--color-fg-subtle)]',
                )}
                aria-hidden
              >
                {f.on ? <ShieldCheck className="h-3 w-3" /> : <Lock className="h-2.5 w-2.5" />}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={cn(
                    'block text-xs',
                    f.on ? 'text-[var(--color-fg-primary)]' : 'text-[var(--color-fg-subtle)] line-through',
                  )}
                >
                  {t(`designJourney.consent.instrument.${f.key}`)}
                  {f.on && f.required ? <span className="text-[var(--color-accent-ink)]"> *</span> : null}
                </span>
                {!f.on && f.motivo ? (
                  <span className="block text-[11px] leading-snug text-[var(--color-fg-subtle)]">{f.motivo}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {config?.brand.source === 'empty' ? (
        <p className="mt-3 text-[11px] leading-snug text-[var(--color-fg-subtle)]">
          {t('designJourney.consent.brandEmpty')}{' '}
          <Link to="/settings/legal" className="font-semibold text-[var(--color-accent-ink)] underline-offset-2 hover:underline">
            {t('designJourney.consent.cta')}
          </Link>
        </p>
      ) : null}
        </>
      )}
    </Card>
  );
}
